/**
 * Guide Module Presence
 *
 * Components for guide presence throughout module learning.
 * Includes intro, section tips, and completion messages.
 *
 * Australian English throughout.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Sparkles, CheckCircle } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';
import {
  getModuleIntro,
  getRandomSectionTip,
  getExerciseEncouragement,
  getModuleCompletion,
} from '../../data/guideModuleContent';

/**
 * Guide Module Intro - Shown at the start of a module
 */
export const GuideModuleIntro = ({ moduleId, className = '' }) => {
  const { selectedGuide, hasCompletedSelection } = useGuide();

  if (!hasCompletedSelection) return null;

  const intro = getModuleIntro(moduleId, selectedGuide.id);
  if (!intro) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className={className}
    >
      <div
        className="rounded-xl p-4 border"
        style={{
          backgroundColor: `${selectedGuide.accentColor}15`,
          borderColor: `${selectedGuide.accentColor}40`,
        }}
      >
        <div className="flex items-start gap-3">
          {/* Guide Avatar */}
          <div
            className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold shadow-md"
            style={{ backgroundColor: selectedGuide.accentColor }}
          >
            {selectedGuide.name[0]}
          </div>

          {/* Message */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-white text-sm">{selectedGuide.name}</span>
              <span className="text-xs text-slate-400">Your Guide</span>
            </div>
            <p className="text-slate-200 text-sm leading-relaxed">
              {intro}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Guide Section Tip - Shown between module sections
 */
export const GuideSectionTip = ({ className = '' }) => {
  const { selectedGuide, hasCompletedSelection } = useGuide();

  if (!hasCompletedSelection) return null;

  const tip = getRandomSectionTip(selectedGuide.id);

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={`flex items-center gap-3 ${className}`}
    >
      <div
        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold"
        style={{ backgroundColor: selectedGuide.accentColor }}
      >
        {selectedGuide.name[0]}
      </div>
      <div className="flex-1 bg-slate-800/60 rounded-lg px-3 py-2 border border-slate-700/50">
        <p className="text-slate-300 text-sm italic">"{tip}"</p>
      </div>
    </motion.div>
  );
};

/**
 * Guide Exercise Encouragement - Shown before exercises
 */
export const GuideExerciseIntro = ({ className = '' }) => {
  const { selectedGuide, hasCompletedSelection } = useGuide();

  if (!hasCompletedSelection) return null;

  const encouragement = getExerciseEncouragement(selectedGuide.id);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={className}
    >
      <div className="flex items-start gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <div
          className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white font-bold"
          style={{ backgroundColor: selectedGuide.accentColor }}
        >
          {selectedGuide.name[0]}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-white">Time to practise!</span>
          </div>
          <p className="text-slate-300 text-sm">{encouragement}</p>
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Guide Module Completion - Shown when finishing a module
 */
export const GuideModuleCompletion = ({ className = '' }) => {
  const { selectedGuide, hasCompletedSelection } = useGuide();

  if (!hasCompletedSelection) return null;

  const message = getModuleCompletion(selectedGuide.id);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5 }}
      className={className}
    >
      <div
        className="rounded-xl p-5 border text-center"
        style={{
          backgroundColor: `${selectedGuide.accentColor}15`,
          borderColor: `${selectedGuide.accentColor}40`,
        }}
      >
        {/* Guide Avatar - larger for celebration */}
        <div className="flex justify-center mb-4">
          <motion.div
            className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg"
            style={{ backgroundColor: selectedGuide.accentColor }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {selectedGuide.name[0]}
          </motion.div>
        </div>

        <div className="flex items-center justify-center gap-2 mb-3">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="font-semibold text-white">{selectedGuide.name} says:</span>
        </div>

        <p className="text-slate-200 leading-relaxed max-w-lg mx-auto">
          {message}
        </p>

        <p className="text-xs text-slate-400 mt-4">
          Chat with {selectedGuide.name} anytime using the button in the corner
        </p>
      </div>
    </motion.div>
  );
};

export default {
  GuideModuleIntro,
  GuideSectionTip,
  GuideExerciseIntro,
  GuideModuleCompletion,
};
